import React, {
  createContext, useContext, useState, useCallback, useEffect, useMemo, useRef,
} from "react";
import axios from "axios";
import { useUser } from "../../users/providers/UserProvider";
import { useSnack } from "../../providers/SnackBarProvider";
import {
  getUserCollection,
  addCardToCollection,
  addBulkToCollection as apiBulkAdd,
  removeCollectionCard,
} from "../../collection/collectionApi";
import { getToken } from "../../users/service/localStorage";
import {
  shouldCelebrateFirstCard,
  markFirstCardCelebrated,
} from "../../collection/firstCardCelebrationLogic";
import FirstCardCelebrationHost from "../../collection/FirstCardCelebrationHost";
import CardRevealMoment from "../../collection/CardRevealMoment";
import { trackEvent } from "../../analytics/analytics";
import { useTradeActions } from "../../trades/providers/TradeActionProvider";
import useAppResume from "../../native/useAppResume";

const CollectionContext = createContext(null);

// ─────────────────────────────────────────────────────────────────────────────
// Helper: extract the populated pokemonCard from a UserCard document.
// Falls back gracefully if a raw pokemonCard slips through (e.g. legacy code).
// ─────────────────────────────────────────────────────────────────────────────
export const getCardData = (userCardOrCard) => {
  if (userCardOrCard?.cardId?._id) return userCardOrCard.cardId;
  return userCardOrCard;
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper: group an array of UserCards into { setId, setName, cards[] } buckets
// sorted alphabetically (unknown set last).
// ─────────────────────────────────────────────────────────────────────────────
const groupBySet = (userCards = []) => {
  const map = {};
  for (const uc of userCards) {
    const card = uc.cardId;
    if (!card) continue;
    const setId = card.set?.id || "unknown";
    const setName = card.set?.name || "Legacy";
    if (!map[setId]) map[setId] = { setId, setName, cards: [] };
    map[setId].cards.push(uc);
  }
  return Object.values(map).sort((a, b) => {
    if (a.setId === "unknown") return 1;
    if (b.setId === "unknown") return -1;
    return a.setName.localeCompare(b.setName);
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────
/** The server's per-request cap on POST /collection/batch. */
const BULK_BATCH_SIZE = 200;

export const CollectionProvider = ({ children }) => {
  const { user } = useUser();
  const snack = useSnack();

  const [collectionByType, setCollectionByType] = useState({ glory: [], trade: [], sale: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  // True once a fetch has landed for this user. Distinguishes "the collection
  // is empty" from "we do not know what the collection is" — `collectionByType`
  // reads the same in both cases, and toggleCollection needs to tell them apart.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [firstCardMoment, setFirstCardMoment] = useState(null);
  const [revealMoment, setRevealMoment] = useState(null);

  // Card ids with an add currently in flight (backlog #90). A ref, not state:
  // nothing renders from it, and a ref is stable, so it stays out of every
  // useCallback dep array below — `addToCollection`'s identity is what
  // `toggleCollection` and the context value are memoised on.
  //
  // Per CARD, deliberately not the `isSaving` boolean above: `isSaving` is true
  // while ANY save is in flight, so gating on it would also refuse an add to a
  // DIFFERENT card. That is a user-visible behaviour change, not a fix.
  const inFlightAdds = useRef(new Set());

  // Ensure the JWT is on axios defaults before any collection API call.
  // CollectionProvider sits above the service hooks that normally call useAxios,
  // so we set the default header ourselves here.
  const ensureAuthHeader = useCallback(() => {
    const t = getToken();
    if (t) axios.defaults.headers.common["x-auth-token"] = t;
  }, []);

  // Loads overlap (entering bulk selection reloads, and so does every add), and
  // an older answer landing last used to put back a collection from before the
  // add. Each load is numbered; an answer older than the one on screen (or
  // from before a change of account) is dropped, and only the newest load ends
  // the loading state.
  const loadSeq = useRef(0);
  const shownSeq = useRef(0);

  // `quiet === true` is a background refresh (B-56): no loading state and no
  // error state, because CollectionPage swaps the whole screen for a loader or
  // an error card on those, which would throw away the collector's scroll and
  // any open sheet. A quiet failure keeps the cards already on screen. Strictly
  // `true`, so a click event passed in by accident is still a normal load.
  // Quiet loads are numbered like any other, so a stale answer is dropped
  // whichever kind of load produced it.
  const loadCollection = useCallback(async (quiet) => {
    if (!user?._id) return;
    const seq = ++loadSeq.current;
    ensureAuthHeader();
    const loud = quiet !== true;
    if (loud) {
      setIsLoading(true);
      setError(null);
    }
    try {
      // Quiet all the way down: the global interceptor raises no snackbar
      // either, or a retry on every poll answer would toast every 30 s (B-61).
      const data = await getUserCollection(user._id, loud ? undefined : { quiet });
      if (seq < shownSeq.current) return;
      shownSeq.current = seq;
      setCollectionByType({
        glory: data.glory || [],
        trade: data.trade || [],
        sale: data.sale || [],
      });
      setHasLoadedOnce(true);
      setError(null);
    } catch (err) {
      if (seq < shownSeq.current) return;
      const msg = typeof err === "string" ? err : err?.message || "Failed to load collection";
      if (loud) setError(msg);
    } finally {
      // Only the newest load ends the loading state. A quiet load never
      // started one, but when it is the newest it still closes one an older
      // loud load opened, which would otherwise be left spinning.
      if (seq === loadSeq.current) setIsLoading(false);
    }
  }, [user?._id, ensureAuthHeader]);

  useEffect(() => {
    // This effect re-runs only when user?._id changes (loadCollection's own deps
    // are user?._id and the stable ensureAuthHeader), and a different id means we
    // know nothing about the new user's collection until its own load lands.
    // A load still out for the previous account must not land on this one.
    shownSeq.current = loadSeq.current + 1;
    setHasLoadedOnce(false);
    if (user?._id) {
      loadCollection();
    } else {
      setCollectionByType({ glory: [], trade: [], sale: [] });
      setError(null);
    }
  }, [user?._id, loadCollection]);

  const refreshCollection = useCallback(() => loadCollection(), [loadCollection]);

  // B-56: the collection used to reload only on sign-in, after this device's
  // own edits, or when THIS device's tap completed a trade. A trade completes
  // on the second confirmation and the server moves the cards then, so the
  // collector who confirmed first kept the card they gave away and missed the
  // one they received until a restart. The badge poll now reports every trade
  // it sees become completed, whoever confirmed last.
  // B-14: and every trade it sees become accepted or stop being accepted,
  // because that is what adds or clears a row's `promisedTo` (the "In a
  // trade" mark, and the remove dialog's refusal) without the cards moving.
  const { completedCount, promiseCount, syncedAt } = useTradeActions();
  useEffect(() => {
    if (completedCount || promiseCount) loadCollection(true);
    // Only a new completion or promise reloads here; a new account has its own
    // load above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedCount, promiseCount]);

  // A failed load retries each time that poll gets an answer, so the error
  // screen goes away once the server is back (B-61).
  useEffect(() => {
    if (error) loadCollection(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncedAt]);

  // And whenever the app comes back to the front (B-50, B-56): the cards may
  // have changed from another phone or a trade in the meantime.
  useAppResume(() => loadCollection(true));

  // ── Derived state ────────────────────────────────────────────────────────
  const collectionCards = useMemo(
    () => [
      ...collectionByType.glory,
      ...collectionByType.trade,
      ...collectionByType.sale,
    ],
    [collectionByType],
  );

  // Contains pokemonCard._id strings (not UserCard._id) for compatibility
  // with all existing `collectionIds.has(card._id)` call-sites.
  const collectionIds = useMemo(
    () => new Set(
      collectionCards
        .filter((uc) => uc.cardId?._id)
        .map((uc) => uc.cardId._id.toString()),
    ),
    [collectionCards],
  );

  // Grouped by set across all types — kept for backwards-compatible consumers.
  const collectionBySets = useMemo(
    () => groupBySet(collectionCards),
    [collectionCards],
  );

  const collectionCount = collectionCards.length;

  const isInCollection = useCallback(
    (cardId) => collectionIds.has(cardId?.toString()),
    [collectionIds],
  );

  /**
   * Open the per-card reveal. Lives here, not in a page, so EVERY add path gets
   * it — the "+" button on a market tile goes through addToCollection, which no
   * page is mounted for. Intensity is decided inside CardRevealMoment from the
   * card's rarity; a bulk card opens nothing at all.
   */
  const revealCard = useCallback(({ card, ownerPhotoUrl } = {}) => {
    if (!card) return;
    setRevealMoment({ card, ownerPhotoUrl });
  }, []);

  /** Call after a successful add that bypassed addToCollection (e.g. ListCardModal). */
  const maybeCelebrateFirstCard = useCallback(
    ({ wasEmpty, imageUrl, cardName, cardId, collectionType } = {}) => {
      if (
        !shouldCelebrateFirstCard({
          userId: user?._id,
          wasEmpty: !!wasEmpty,
          isLoading: false,
        })
      ) {
        return false;
      }
      markFirstCardCelebrated(user._id);
      // cardId + collectionType let the moment offer "List it for trade" when
      // the first card went to the Vault (P0 area B).
      setFirstCardMoment({ imageUrl, cardName, cardId, collectionType });
      return true;
    },
    [user?._id],
  );

  // ── Mutations ────────────────────────────────────────────────────────────
  const addToCollection = useCallback(
    async (cardId, collectionType = "glory", metadata = {}) => {
      if (!user) { snack("error", "Please log in to use your collection"); return; }
      ensureAuthHeader();
      const wasEmpty = !isLoading && collectionCount === 0;
      // `card` is display-only (rarity for the reveal) — strip it with the rest
      // so it never reaches the API payload.
      const { cardImage, cardName, card: revealSource, ...apiMeta } = metadata || {};
      // Backlog #90 — two taps on the same tile's "+" inside one round-trip both
      // reach the server before either has written, the server's
      // read-then-insert de-duplication sees nothing both times, and the card
      // ends up with two UserCard rows. `resolveOwnedCopy` then answers
      // `ambiguous` for that card, and PUT and DELETE /collection/:cardId return
      // 409 AMBIGUOUS_COPY on it — the card can no longer be edited or removed.
      //
      // No server-side half ships with this. The atomic-upsert repair was
      // REVERTED — it discarded the losing request's payload and skipped the
      // schema validators; the #90 note in
      // server/__tests__/collection.integration.test.js records what a durable
      // one would need. So the race inside `addToCollection`
      // (server/collection/collectionController.js) is still open and this
      // guard is NOT the durable fix: it cannot see a second tab or a second
      // device. What it does close is the case that actually happens — two taps
      // on one tile in one tab. It saves the round-trip, and it keeps a double
      // tap from firing `card_added` twice, which the analytics middleware
      // emits for every POST /collection that answers under 400
      // (server/analytics/analyticsMiddleware.js), whatever the row count does.
      //
      // Claimed immediately before the `try`, with only the synchronous
      // `setIsSaving(true)` in between, so nothing can throw or await between
      // taking the claim and entering the block whose `finally` releases it.
      // That release runs on the rejected path as well as the resolved one — a
      // claim that leaked would block re-adding that card for the whole session.
      const addKey = String(cardId);
      if (inFlightAdds.current.has(addKey)) return;
      inFlightAdds.current.add(addKey);
      setIsSaving(true);
      try {
        await addCardToCollection({ cardId, collectionType, ...apiMeta });
        await loadCollection();
        // `card_added` is emitted server-side by the analytics middleware on
        // POST /collection. Firing it here too double-counted the single most
        // important activation metric — and biased it upward, which is the
        // direction that makes a beta look like it is working when it isn't.
        //
        // `first_card_added` stays: it depends on the collection having been
        // empty *before* the call, which only the client knows here.
        if (wasEmpty) trackEvent("first_card_added", { collectionType });
        const label = collectionType === "glory" ? "Vault"
          : collectionType === "trade" ? "Trade Binder" : "For Sale";
        snack("success", `Added to ${label}`);
        const celebratedFirst = maybeCelebrateFirstCard({
          wasEmpty,
          imageUrl: cardImage,
          cardName,
          cardId,
          collectionType,
        });
        // Never stack two full-screen moments — first-card wins outright.
        if (!celebratedFirst) {
          // Deliberately no `ownerPhotoUrl` (#295). This is the "+" path, and
          // on it `cardImage` is the card's own CATALOGUE art: `addToCollection`
          // is exposed on the context (see the value below), but its only
          // production caller today is `toggleCollection`, which builds it as
          // `card?.images?.small || card?.images?.large` from the very object it
          // forwards here as `revealSource`. Handing that to a prop whose name
          // asserts "a photo the owner took" makes the lie inheritable by any
          // consumer that branches on the prop being present.
          //
          // Dropping it changes no pixels: CardRevealMoment resolves its picture
          // as `ownerPhotoUrl || card?.images?.small || card?.images?.large`, and
          // the value removed here was that same fallback.
          //
          // The PARAMETER is not the problem and stays — ListCardModal calls
          // `revealCard` with a real uploaded photo, which is what the name is
          // for.
          revealCard({ card: revealSource });
        }
      } catch (err) {
        snack("error", typeof err === "string" ? err : err?.message || "Failed to add card");
      } finally {
        inFlightAdds.current.delete(addKey);
        setIsSaving(false);
      }
    },
    // `inFlightAdds` is a ref, so it is stable and deliberately absent here —
    // adding it would be noise, and adding the Set itself would defeat the guard
    // by re-creating this callback.
    [user, snack, ensureAuthHeader, loadCollection, isLoading, collectionCount, maybeCelebrateFirstCard, revealCard],
  );

  const removeFromCollection = useCallback(
    async (cardId) => {
      if (!user) return;
      ensureAuthHeader();
      setIsSaving(true);
      try {
        await removeCollectionCard(cardId);
        await loadCollection();
        snack("info", "Removed from collection");
      } catch (err) {
        snack("error", typeof err === "string" ? err : err?.message || "Failed to remove card");
      } finally {
        setIsSaving(false);
      }
    },
    [user, snack, ensureAuthHeader, loadCollection],
  );

  const addBulkToCollection = useCallback(
    async (cardIds) => {
      if (!user) { snack("error", "Please log in to use your collection"); return null; }
      if (!cardIds?.length) return null;
      ensureAuthHeader();
      setIsSaving(true);
      try {
        // The server takes at most 200 cards per request ("Maximum 200 cards
        // per batch"); sending a big set at once added nothing (E2E audit
        // 15·9b). Split like the wishlist does, and add the counts up.
        let addedSoFar = 0;
        let skippedSoFar = 0;
        for (let i = 0; i < cardIds.length; i += BULK_BATCH_SIZE) {
          const batch = cardIds.slice(i, i + BULK_BATCH_SIZE);
          let part;
          try {
            part = await apiBulkAdd(batch);
          } catch (err) {
            if (addedSoFar > 0) {
              await loadCollection();
              const detail = typeof err === "string" ? err : err?.message || "Failed to add cards";
              snack("error", `Added ${addedSoFar} of ${cardIds.length} cards, then failed: ${detail}`);
              return null;
            }
            throw err;
          }
          addedSoFar += part?.added ?? 0;
          skippedSoFar += part?.skipped ?? 0;
        }
        const result = { added: addedSoFar, skipped: skippedSoFar, total: cardIds.length };
        await loadCollection();
        // B-34: the server skips cards you already own and says how many
        // (`skipped`). "Add 2 cards" used to report "Added 1 card" and leave the
        // user to guess what happened to the other one.
        const { added = 0, skipped = 0 } = result || {};
        const owned = `${skipped} ${skipped === 1 ? "was" : "were"} already in your collection`;
        if (!added && skipped) snack("info", `Nothing added: ${owned}.`);
        else {
          snack(
            "success",
            `Added ${added} card${added !== 1 ? "s" : ""} to your Vault${skipped ? `. ${owned}.` : ""}`,
          );
        }
        return result;
      } catch (err) {
        snack("error", typeof err === "string" ? err : err?.message || "Failed to add cards");
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [user, snack, ensureAuthHeader, loadCollection],
  );

  // toggleCollection preserves the old call signature (card object or cardId).
  // Called with a pokemonCard from PokemonMain / WishlistPage:
  //   - If already in collection → remove
  //   - If not → add as "glory" (safest default; user can re-list from CollectionPage)
  const toggleCollection = useCallback(
    async (card) => {
      if (!user) { snack("error", "Please log in to use your collection"); return; }
      const cardId = (card?._id ?? card)?.toString();
      if (!cardId) return;
      // Until a load has landed, `collectionIds` is empty, so `has(cardId)` says
      // "not owned" for every card — including ones already listed For Sale or
      // for Trade. The add branch below posts { cardId, collectionType: "glory" }
      // with no asNewCopy, which the server merges onto the existing UserCard
      // row, so guessing here re-files a live listing as a Vault card and then
      // reports "Added to Vault". Refuse rather than guess.
      if (!hasLoadedOnce) {
        if (error) refreshCollection();
        snack(
          "error",
          error
            ? "Couldn't load your collection — retrying, try again in a moment"
            : "Still loading your collection — try again in a moment",
        );
        return;
      }
      if (collectionIds.has(cardId)) {
        await removeFromCollection(cardId);
      } else {
        await addToCollection(cardId, "glory", {
          cardImage: card?.images?.small || card?.images?.large,
          cardName: card?.name,
          // The reveal reads rarity off this. Without it every add would
          // silently fall to the quiet tier and show nothing.
          card: typeof card === "object" ? card : null,
        });
      }
    },
    [user, snack, collectionIds, hasLoadedOnce, error, refreshCollection,
      addToCollection, removeFromCollection],
  );

  // ── Context value ────────────────────────────────────────────────────────
  const value = useMemo(
    () => ({
      collectionByType,
      collectionCards,
      collectionIds,
      collectionBySets,
      collectionCount,
      isLoading,
      // `isLoading` starts false, so "not loading" is also true BEFORE the first
      // fetch. Anything that decides from the collection's contents (the
      // Collection page's opening tab) must wait for this instead.
      hasLoadedOnce,
      isSaving,
      error,
      refreshCollection,
      addToCollection,
      addBulkToCollection,
      removeFromCollection,
      toggleCollection,
      isInCollection,
      maybeCelebrateFirstCard,
      revealCard,
    }),
    [
      collectionByType, collectionCards, collectionIds, collectionBySets,
      collectionCount, isLoading, hasLoadedOnce, isSaving, error,
      refreshCollection, addToCollection, addBulkToCollection, removeFromCollection,
      toggleCollection, isInCollection, maybeCelebrateFirstCard, revealCard,
    ],
  );

  return (
    <CollectionContext.Provider value={value}>
      {children}
      <FirstCardCelebrationHost
        open={!!firstCardMoment}
        onClose={() => setFirstCardMoment(null)}
        imageUrl={firstCardMoment?.imageUrl}
        cardName={firstCardMoment?.cardName}
        cardId={firstCardMoment?.cardId}
        collectionType={firstCardMoment?.collectionType}
      />
      {/* Hosted beside the first-card moment so every add path is covered,
          including the "+" button on a market tile, which no page owns. */}
      <CardRevealMoment
        open={!!revealMoment}
        card={revealMoment?.card}
        ownerPhotoUrl={revealMoment?.ownerPhotoUrl}
        onClose={() => setRevealMoment(null)}
      />
    </CollectionContext.Provider>
  );
};

export const useCollection = () => {
  const ctx = useContext(CollectionContext);
  if (!ctx) throw new Error("useCollection must be used within CollectionProvider");
  return ctx;
};
